import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined';
import {
  Card,
  CardContent,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium';
import { useGetGuestJourneySettingsHistoryQuery } from 'api/client-domain/client-domain';
import { GuestJourneySettingsHistoryEntry } from 'api/reports-service/guest-journey-reports.types';
import { useDrawer } from 'components/drawers/inc-drawer/inc-drawer.hooks';
import IncDataGrid from 'components/inc-data-grid/inc-data-grid.component';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import GuestJourneySettingsChangesDrawer from './guest-journey-settings-changes-drawer.component';

export type GuestJourneySettingsHistoryProps = {
  'data-testid'?: string;
};

/**
 * The "View Changes" audit trail — a table of prior settings saves (date +
 * admin user), newest first. Each row's eye button opens the "Threshold
 * Changes" drawer with the before/after diff for that revision.
 */
const GuestJourneySettingsHistory = ({
  'data-testid': dataTestId = 'GuestJourney.Settings.History',
}: GuestJourneySettingsHistoryProps) => {
  const { t } = useTranslation();
  const { data, isFetching } = useGetGuestJourneySettingsHistoryQuery();
  const changesDrawer = useDrawer<GuestJourneySettingsHistoryEntry>();
  const { open: openChanges } = changesDrawer;

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'changedAt',
        headerName: t('page.guestJourney.settings.history.columns.date'),
        flex: 1,
        minWidth: 200,
        sortable: false,
        valueFormatter: (value: string) =>
          DateTime.fromISO(value).toUTC().toFormat('yyyy-MM-dd HH:mm:ss'),
      },
      {
        field: 'changedByName',
        headerName: t('page.guestJourney.settings.history.columns.adminUser'),
        flex: 1,
        minWidth: 160,
        sortable: false,
      },
      {
        field: 'changes',
        headerName: '',
        width: 64,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (
          params: GridRenderCellParams<GuestJourneySettingsHistoryEntry>,
        ) => (
          <IconButton
            size="small"
            onClick={() => openChanges(params.row)}
            aria-label={t('page.guestJourney.settings.history.viewChange')}
            data-testid={`${dataTestId}.View.${params.row.id}`}
          >
            <VisibilityOutlined fontSize="small" />
          </IconButton>
        ),
      },
    ],
    [t, openChanges, dataTestId],
  );

  return (
    <Card data-testid={dataTestId}>
      <CardContent>
        <Stack spacing={1}>
          <Typography variant="h2">
            {t('page.guestJourney.settings.history.title')}
          </Typography>
          <IncDataGrid
            data-testid={`${dataTestId}.Grid`}
            columns={columns}
            rows={data?.entries ?? []}
            loading={isFetching}
            getRowId={row => row.id}
            hideFooter
            disableColumnReorder
            disableColumnMenu
            disableRowSelectionOnClick
            autoHeight
          />
        </Stack>
      </CardContent>
      <GuestJourneySettingsChangesDrawer {...changesDrawer} />
    </Card>
  );
};

export default GuestJourneySettingsHistory;
